import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import creditUnionTheme from "./creditUnionTheme";
import MoneyMatch3Game from "./MoneyMatch3Game";
import SmartMoneyThisOrThat from "./SmartMoneyThisOrThat";
import FinancialTriviaChallenge from "./FinancialTriviaChallenge";
import MoneyMomentum from "./SavingsStreak";
import BaseMemoryGame from "../../games/Memory/BaseMemoryGame";
import AboutUs from "../About";
import Footer from "../Footer";

const VALUE_TILES = [
  {
    icon: "🎯",
    title: "Boost Member Engagement",
    desc: "Turn passive visits into active participation with daily branded games.",
  },
  {
    icon: "💡",
    title: "Reinforce Financial Wellness",
    desc: "Make savings, budgeting, and smart money habits interactive and fun.",
  },
  {
    icon: "🚀",
    title: "Easy to Launch & Maintain",
    desc: "We handle design, branding, and setup — you get a custom solution in days.",
  },
];

const HOW_IT_WORKS = [
  {
    icon: "🎲",
    title: "Choose a Game Type",
    desc: "Pick from member-favorite formats like Match-3, Trivia, or Spin-to-Win.",
  },
  {
    icon: "🎨",
    title: "We Brand It for You",
    desc: "Your logo, colors, and tone — fully customized with no extra work.",
  },
  {
    icon: "🌐",
    title: "You Deploy & Engage Members",
    desc: "Embed in your website, app, or newsletter. Simple for you, delightful for members.",
  },
];

const FAQS = [
  {
    q: "Will this distract from banking tasks?",
    a: "Not at all. Games are designed to enhance engagement moments, not interrupt key actions.",
    icon: "🧩",
  },
  {
    q: "Is this safe for all ages?",
    a: "Yes. All games are family-friendly, ad-free, and privacy-respecting.",
    icon: "👨‍👩‍👧‍👦",
  },
  {
    q: "How fast can we launch?",
    a: "You can be live in as little as 7 days — fully branded, hosted by us.",
    icon: "⚡️",
  },
  {
    q: "Do we need to install anything?",
    a: "No downloads or IT integration needed. We handle everything.",
    icon: "☁️",
  },
];

const CreditUnionHome = () => {
  const theme = useTheme();

  // Move these inside the component so theme is defined
  const FINANCIAL_MEMORY_ITEMS = [
    { id: 1, title: "Savings", image: "💰" },
    { id: 2, title: "Credit Card", image: "💳" },
    { id: 3, title: "Bank", image: "🏦" },
    { id: 4, title: "Stocks", image: "📈" },
    { id: 5, title: "Wallet", image: "💼" },
    { id: 6, title: "Coins", image: "🪙" },
    { id: 7, title: "Cash", image: "💵" },
    { id: 8, title: "Piggy Bank", image: "🐷" },
  ];

  const GAMES = [
    {
      key: "match3",
      title: "Money Match3",
      description:
        "Match financial symbols and learn money concepts while having fun!",
      component: <MoneyMatch3Game />,
      badge: "Fan Favorite",
      badgeColor: theme.palette.secondary.main,
      badgeTextColor: theme.palette.primary.main,
      borderColor: theme.palette.secondary.main,
      tags: [
        {
          label: "Family Friendly",
          color: theme.palette.primary.main,
          bg: "rgba(30,64,175,0.08)",
        },
        {
          label: "Quick Play",
          color: theme.palette.secondary.main,
          bg: "rgba(37,99,166,0.08)",
        },
      ],
    },
    {
      key: "thisorthat",
      title: "This or That: Smart Money Edition",
      description:
        'Make smart financial choices through fun "Would you rather" scenarios!',
      component: <SmartMoneyThisOrThat />,
      borderColor: theme.palette.primary.main,
      tags: [
        {
          label: "Social",
          color: theme.palette.primary.main,
          bg: "rgba(30,64,175,0.08)",
        },
      ],
    },
    {
      key: "trivia",
      title: "Financial Trivia Challenge",
      description:
        "Test your financial knowledge with quick, educational quizzes on savings, credit, and budgeting!",
      component: <FinancialTriviaChallenge />,
      borderColor: theme.palette.primary.main,
      tags: [
        {
          label: "Educational",
          color: theme.palette.primary.main,
          bg: "rgba(30,64,175,0.08)",
        },
      ],
    },
    {
      key: "memory",
      title: "Memory Match",
      description: "Quick, fun memory games with financial icons.",
      component: (
        <BaseMemoryGame
          title="Memory Match"
          subtitle="Match the financial icon to its name!"
          shareText="I just played Memory Match on Flying Comet Games!"
          shareUrl={typeof window !== "undefined" ? window.location.href : ""}
          successBackgroundColor="#91B2D1"
          shareButtonColor={[theme.palette.primary.main, "#fff"]}
          customItems={FINANCIAL_MEMORY_ITEMS}
          publicationId="CreditUnionMemoryGame"
          hideTitle={false}
          showDatePicker={false}
        />
      ),
      borderColor: theme.palette.primary.main,
      tags: [
        {
          label: "Family Friendly",
          color: theme.palette.primary.main,
          bg: "rgba(30,64,175,0.08)",
        },
      ],
    },
  ];

  return (
    <ThemeProvider theme={creditUnionTheme}>
      <Box
        sx={{ bgcolor: theme.palette.background.default, minHeight: "100vh" }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: theme.customPalette.heroBg,
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 0 },
            textAlign: "center",
            borderBottom: `1px solid ${theme.customPalette.tileBorder}`,
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: "auto", textAlign: "center" }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2.2rem", md: "3.5rem" },
                color: theme.palette.primary.main,
                mb: 2,
              }}
            >
              Branded Daily Games to Keep Your Members Engaged
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 400,
                fontSize: { xs: "1.1rem", md: "1.4rem" },
                color: theme.palette.text.primary,
                mb: 4,
                opacity: 0.9,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              We create simple, interactive games credit unions use to boost
              retention, drive digital engagement, and support financial
              wellness — no heavy IT lift required.
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                px: 6,
                py: 2.5,
                borderRadius: "100px",
                fontSize: "1.2rem",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: theme.shadows[1],
                mb: 2,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.primary.main,
                },
              }}
            >
              Get a Custom Demo
            </Button>
            <Typography
              sx={{
                color: theme.palette.text.primary,
                mt: 1,
                mb: 4,
                fontSize: "1rem",
                opacity: 0.7,
              }}
            >
              No downloads. Ready to deploy in 7 days.
            </Typography>
            {/* Game Demo Widget */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                minHeight: 600,
              }}
            >
              <MoneyMatch3Game />
            </Box>
          </Box>
        </Box>

        {/* Value Proposition Tiles */}
        <Box
          py={10}
          sx={{
            bgcolor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.customPalette.tileBorder}`,
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "2.7rem" },
                color: theme.palette.primary.main,
                textAlign: "center",
                mb: 6,
              }}
            >
              Why Credit Unions Choose Flying Comet
            </Typography>
            <Grid
              container
              spacing={4}
              justifyContent="center"
              columns={{ xs: 4, sm: 8, md: 12 }}
            >
              {VALUE_TILES.map((tile, idx) => (
                <Grid key={tile.title} size={{ xs: 4, sm: 4, md: 4 }}>
                  <Box
                    sx={{
                      bgcolor: theme.palette.background.default,
                      border: `1px solid ${theme.customPalette.tileBorder}`,
                      borderRadius: theme.shape.borderRadius,
                      p: 5,
                      textAlign: "center",
                      boxShadow: theme.shadows[1],
                      height: "100%",
                      transition: "all 0.3s",
                      "&:hover": {
                        boxShadow: "0 8px 32px rgba(37,99,166,0.08)",
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: "2.5rem", mb: 2 }}>
                      {tile.icon}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {tile.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.text.primary,
                        fontSize: "1.1rem",
                        opacity: 0.85,
                      }}
                    >
                      {tile.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* How It Works Section */}
        <Box
          py={10}
          sx={{
            bgcolor: "#E8F4FA",
            borderBottom: `1px solid ${theme.customPalette.tileBorder}`,
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "2.7rem" },
                color: theme.palette.primary.main,
                textAlign: "center",
                mb: 6,
              }}
            >
              How It Works
            </Typography>
            <Grid
              container
              spacing={4}
              justifyContent="center"
              columns={{ xs: 4, sm: 8, md: 12 }}
            >
              {HOW_IT_WORKS.map((step, idx) => (
                <Grid key={step.title} size={{ xs: 4, sm: 4, md: 4 }}>
                  <Box
                    sx={{
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.customPalette.tileBorder}`,
                      borderRadius: theme.shape.borderRadius,
                      p: 5,
                      textAlign: "center",
                      boxShadow: theme.shadows[1],
                      height: "100%",
                    }}
                  >
                    <Typography sx={{ fontSize: "2.5rem", mb: 2 }}>
                      {step.icon}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.text.primary,
                        fontSize: "1.1rem",
                        opacity: 0.85,
                      }}
                    >
                      {step.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* Games Showcase Placeholder Section */}
        <Box
          py={10}
          sx={{
            bgcolor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.customPalette.tileBorder}`,
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "2.7rem" },
                color: theme.palette.primary.main,
                textAlign: "center",
                mb: 6,
              }}
            >
              Games Your Members Will Love
            </Typography>
            <Grid
              container
              spacing={4}
              justifyContent="center"
              alignItems="stretch"
              sx={{
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "100%",
                  height: "100%",
                  background:
                    "radial-gradient(circle, rgba(145,178,209,0.08) 0%, transparent 70%)",
                  zIndex: 0,
                },
              }}
            >
              {GAMES.map((game, idx) => (
                <Grid
                  key={game.key}
                  item
                  size={{ xs: 12, sm: 6, md: 6, lg: 6 }}
                  sx={{ display: "flex" }}
                >
                  <Box
                    sx={{
                      bgcolor: theme.palette.background.paper,
                      border: `2px solid ${game.borderColor}`,
                      borderRadius: "24px",
                      p: 4,
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      boxShadow: theme.shadows[1],
                      position: "relative",
                      overflow: "hidden",
                      justifyContent: "space-between",
                      alignItems: "stretch",
                      transition: "all 0.3s",
                      "&:hover": {
                        boxShadow: "0 8px 32px rgba(37,99,166,0.08)",
                        borderColor: theme.palette.primary.main,
                        transform: "translateY(-8px)",
                      },
                    }}
                  >
                    {game.badge && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 24,
                          background: game.badgeColor,
                          color: game.badgeTextColor,
                          px: 2,
                          py: 0.5,
                          borderRadius: "20px",
                          fontSize: "0.9rem",
                          fontWeight: 700,
                          zIndex: 2,
                          boxShadow: "0 4px 12px rgba(145,178,209,0.15)",
                        }}
                      >
                        {game.badge}
                      </Box>
                    )}
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        mb: 2,
                      }}
                    >
                      {game.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.text.primary,
                        mb: 3,
                        fontSize: "1.1rem",
                        opacity: 0.85,
                      }}
                    >
                      {game.description}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {game.component}
                    </Box>
                    <Box
                      sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}
                    >
                      {game.tags.map((tag) => (
                        <Box
                          key={tag.label}
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: "20px",
                            background: tag.bg,
                            color: tag.color,
                            fontSize: "0.9rem",
                            fontWeight: 600,
                          }}
                        >
                          {tag.label}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* Mid-Page CTA Section */}
        <Box
          py={12}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
            color: theme.palette.primary.contrastText,
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: "auto", textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "2.7rem" },
                color: theme.palette.primary.contrastText,
                mb: 2,
              }}
            >
              Want to See More Games?
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "1.1rem", md: "1.3rem" },
                color: theme.palette.primary.contrastText,
                mb: 4,
                opacity: 0.95,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Schedule a personalized demo to explore our full collection of
              branded games and engagement tools.
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.contrastText,
                color: theme.palette.primary.main,
                px: 6,
                py: 2.5,
                borderRadius: "100px",
                fontSize: "1.2rem",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: theme.shadows[1],
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.primary.contrastText,
                },
              }}
            >
              Schedule Demo
            </Button>
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box
          py={10}
          sx={{
            bgcolor: theme.customPalette.faqBg,
            borderBottom: `1px solid ${theme.customPalette.tileBorder}`,
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "2.7rem" },
                color: theme.palette.primary.main,
                textAlign: "center",
                mb: 6,
              }}
            >
              Questions We Hear Often
            </Typography>
            {FAQS.map((faq, idx) => (
              <Box
                key={faq.q}
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderRadius: theme.shape.borderRadius,
                  p: 4,
                  mb: 3,
                  border: `1px solid ${theme.customPalette.tileBorder}`,
                  boxShadow: theme.shadows[1],
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: `${theme.palette.primary.main}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ fontSize: "2rem", lineHeight: 1 }}>
                    {faq.icon}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "1.15rem",
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    {faq.q}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.05rem",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {faq.a}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <AboutUs isComponent={true} />

        {/* Final CTA Section */}
        <Box
          py={12}
          sx={{
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`,
            color: theme.palette.primary.contrastText,
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: "auto", textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "2.7rem" },
                color: theme.palette.primary.contrastText,
                mb: 2,
              }}
            >
              Ready to Boost Member Engagement?
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "1.1rem", md: "1.3rem" },
                color: theme.palette.primary.contrastText,
                mb: 4,
                opacity: 0.95,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              See how Flying Comet can help your credit union increase retention
              and delight members with branded daily games.
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.contrastText,
                color: theme.palette.primary.main,
                px: 6,
                py: 2.5,
                borderRadius: "100px",
                fontSize: "1.2rem",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: theme.shadows[1],
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.primary.contrastText,
                },
              }}
            >
              Get a Custom Demo
            </Button>
          </Box>
        </Box>

        <Footer />
      </Box>
    </ThemeProvider>
  );
};

export default CreditUnionHome;
